/**
 * useIntegrations
 *
 * Fetches team-wide platform connections (HubSpot, future: Shopify, WordPress)
 * and provides a lookup function to check which integrations a domain has.
 *
 * Designed to scale — add new platforms by extending the Integration type
 * and adding a query for that platform's connections.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hubspotApi } from '@/services/hubspot'
import { useTeamContext } from '@/contexts/TeamContext'
import { normalizeDomainForMatching, extractRootFromDomain } from '@/utils/domain'

export type IntegrationPlatform = 'hubspot' | 'shopify' | 'wordpress'

export interface Integration {
  platform: IntegrationPlatform
  connectionId: string
  portalId?: string
  portalName: string | null
  isLinked: boolean // true = domain is in associatedDomains, false = connection exists but domain not added
  /** Subdomains linked to this connection under the same root domain */
  linkedSubdomains?: string[]
}

export function useIntegrations() {
  const { currentTeam } = useTeamContext()
  const teamId = currentTeam?.team?.id

  const { data: hubspotData, isLoading } = useQuery({
    queryKey: ['hubspot-team-connections', teamId],
    queryFn: () => hubspotApi.getTeamConnections(),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes — connections change rarely
  })

  const connections = hubspotData?.data || []
  const hasAnyHubSpotConnection = connections.length > 0

  // Build a Map<normalizedDomain, Integration[]> for fast lookups
  // Deduplicates by (portalId, rootDomain) — merges subdomains across connections
  // that share the same HubSpot portal (e.g., multiple team members)
  const integrationsByDomain = useMemo(() => {
    const map = new Map<string, Integration[]>()

    for (const conn of connections) {
      // Group associated domains by their root domain
      const rootToSubdomains = new Map<string, Set<string>>()

      for (const domain of conn.associatedDomains || []) {
        const normalized = normalizeDomainForMatching(domain) // strips protocol, path, www
        const rootKey = extractRootFromDomain(normalized)
        const existing = rootToSubdomains.get(rootKey) || new Set<string>()
        // Only add true subdomains (not root domain itself)
        if (normalized !== rootKey) {
          existing.add(normalized)
        }
        rootToSubdomains.set(rootKey, existing)
      }

      // Merge into map — deduplicate by portal ID, not connection ID
      for (const [rootKey, subdomainSet] of rootToSubdomains) {
        const existing = map.get(rootKey) || []
        const portalMatch = conn.hubspotPortalId
          ? existing.find(e => e.portalId === conn.hubspotPortalId)
          : null // Don't merge connections with unknown portal IDs

        if (portalMatch) {
          // Same portal from a different team member — merge subdomains
          if (portalMatch.linkedSubdomains) {
            for (const sub of subdomainSet) {
              if (!portalMatch.linkedSubdomains.includes(sub)) {
                portalMatch.linkedSubdomains.push(sub)
              }
            }
            portalMatch.linkedSubdomains.sort()
          } else if (subdomainSet.size > 0) {
            portalMatch.linkedSubdomains = [...subdomainSet].sort()
          }
        } else {
          // New portal for this root domain
          const subs = [...subdomainSet].sort()
          existing.push({
            platform: 'hubspot',
            connectionId: conn.id,
            portalId: conn.hubspotPortalId,
            portalName: conn.portalName,
            isLinked: true,
            linkedSubdomains: subs.length > 0 ? subs : undefined,
          })
          map.set(rootKey, existing)
        }
      }
    }

    return map
  }, [connections])

  /** Get integrations for a specific domain. Returns linked integrations + unlinked prompts. */
  function getIntegrationsForDomain(domainName: string): Integration[] {
    const key = normalizeDomainForMatching(domainName)
    const linked = integrationsByDomain.get(key) || []

    // If domain has no linked HubSpot connection but the team has one,
    // return an unlinked prompt so the UI can show "Link to HubSpot"
    if (linked.length === 0 && hasAnyHubSpotConnection) {
      return [{
        platform: 'hubspot',
        connectionId: connections[0].id,
        portalName: connections[0].portalName,
        isLinked: false,
      }]
    }

    return linked
  }

  return {
    getIntegrationsForDomain,
    integrationsByDomain,
    hasAnyHubSpotConnection,
    isLoading,
  }
}
